import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useJsApiLoader, type Libraries } from '@react-google-maps/api';
import { GOOGLE_MAPS_API_KEY } from '@/lib/google-maps';
import { useLocation } from 'react-router-dom';

interface GoogleMapsContextType {
  isLoaded: boolean;
  loadError: Error | undefined;
  requestLoad: () => void;
}

const GoogleMapsContext = createContext<GoogleMapsContextType>({
  isLoaded: false,
  loadError: undefined,
  requestLoad: () => {},
});

export function useGoogleMaps() {
  return useContext(GoogleMapsContext);
}

const libraries: Libraries = ['places'];

function GoogleMapsScriptLoader({ onStateChange }: { onStateChange: (next: { isLoaded: boolean; loadError: Error | undefined }) => void }) {
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-maps-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries,
    version: 'weekly',
  });

  useEffect(() => {
    onStateChange({ isLoaded, loadError });
  }, [isLoaded, loadError, onStateChange]);

  return null;
}

export function GoogleMapsProvider({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const [shouldLoad, setShouldLoad] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState<Error | undefined>(undefined);

  const requestLoad = () => setShouldLoad(true);

  useEffect(() => {
    if (shouldLoad) return;

    const needsMapsSoon = /\/create\/post|\/transportation|\/hotel|\/rentals|\/events|\/marketplace|\/booking/i.test(pathname);
    if (needsMapsSoon) {
      setShouldLoad(true);
      return;
    }

    const timeoutId = window.setTimeout(() => setShouldLoad(true), 2500);
    return () => window.clearTimeout(timeoutId);
  }, [pathname, shouldLoad]);

  useEffect(() => {
    if (shouldLoad) return;

    const activate = () => setShouldLoad(true);
    window.addEventListener('pointerdown', activate, { once: true });
    window.addEventListener('keydown', activate, { once: true });
    window.addEventListener('touchstart', activate, { once: true });

    return () => {
      window.removeEventListener('pointerdown', activate);
      window.removeEventListener('keydown', activate);
      window.removeEventListener('touchstart', activate);
    };
  }, [shouldLoad]);

  const contextValue = useMemo(
    () => ({ isLoaded, loadError, requestLoad }),
    [isLoaded, loadError]
  );

  return (
    <GoogleMapsContext.Provider value={contextValue}>
      {shouldLoad ? (
        <GoogleMapsScriptLoader
          onStateChange={({ isLoaded: nextLoaded, loadError: nextError }) => {
            setIsLoaded(nextLoaded);
            setLoadError(nextError);
          }}
        />
      ) : null}
      {children}
    </GoogleMapsContext.Provider>
  );
}